/* ═══════════════════════════════════════════════════════════════
   ViewsMenu — saved-views dropdown with full CRUD: apply, save-as,
   update active, rename, delete, set-default, share-toggle. Drift
   indicator shows "(modified)" when current config differs from the
   saved active view so users never confuse dirty state for committed.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef, useState } from 'react';
import type { DashboardView } from '../../../shared/entities';
import { chipBtn, miniIconBtn, textInput, viewsSectionHead } from './_shared';

export function ViewsMenu({
  savedViews,
  activeViewId,
  currentUserId,
  viewIsDirty,
  onApplyView,
  onSaveAs,
  onUpdateActive,
  onDeleteView,
  onSetDefaultView,
  onRenameView,
  onShareToggle,
}: {
  savedViews: DashboardView[];
  activeViewId: string | null;
  currentUserId: string;
  viewIsDirty: boolean;
  onApplyView: (v: DashboardView) => void;
  onSaveAs: () => void;
  onUpdateActive: () => Promise<void>;
  onDeleteView: (id: string) => Promise<void>;
  onSetDefaultView: (id: string) => Promise<void>;
  onRenameView: (id: string, name: string) => Promise<void>;
  onShareToggle: (id: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setRenamingId(null);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const active = savedViews.find((v) => v.id === activeViewId);
  const ownViews = savedViews.filter((v) => v.userId === currentUserId);
  const sharedViews = savedViews.filter((v) => v.userId !== currentUserId && v.shared);

  function startRename(v: DashboardView) {
    setRenamingId(v.id);
    setRenameValue(v.name);
  }
  async function commitRename() {
    if (renamingId && renameValue.trim()) {
      await onRenameView(renamingId, renameValue.trim());
    }
    setRenamingId(null);
  }

  function renderRow(v: DashboardView, mine: boolean) {
    const isActive = v.id === activeViewId;
    if (renamingId === v.id) {
      return (
        <div key={v.id} style={{ padding: '6px 12px', borderBottom: '1px solid var(--bd)' }}>
          <input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              else if (e.key === 'Escape') setRenamingId(null);
            }}
            onBlur={commitRename}
            autoFocus
            style={{ ...textInput, fontSize: 12, padding: '4px 8px' }}
          />
        </div>
      );
    }
    return (
      <div
        key={v.id}
        style={{
          padding: '6px 12px',
          fontSize: 12,
          background: isActive ? 'var(--sa)' : '',
          color: 'var(--tx)',
          fontWeight: isActive ? 600 : 500,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = isActive ? 'var(--sa)' : '')}
      >
        <span
          onClick={() => {
            onApplyView(v);
            setOpen(false);
          }}
          style={{
            flex: 1,
            cursor: 'pointer',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={v.name}
        >
          {v.isDefault && <span style={{ color: 'var(--ac)', marginRight: 4 }}>★</span>}
          {v.name}
          {v.shared && (
            <span style={{ fontSize: 9, color: 'var(--tm)', marginLeft: 6 }}>· shared</span>
          )}
        </span>
        {mine && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSetDefaultView(v.id);
              }}
              title={v.isDefault ? 'Default view (loaded on open)' : 'Set as default'}
              style={miniIconBtn}
              aria-label="Set as default"
            >
              {v.isDefault ? '★' : '☆'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onShareToggle(v.id);
              }}
              title={v.shared ? 'Shared with firm — click to unshare' : 'Share with firm'}
              style={miniIconBtn}
              aria-label="Toggle share"
            >
              {v.shared ? '👥' : '🔒'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                startRename(v);
              }}
              title="Rename"
              style={miniIconBtn}
              aria-label="Rename view"
            >
              ✎
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteView(v.id);
              }}
              title="Delete"
              style={{ ...miniIconBtn, color: 'var(--dg)' }}
              aria-label="Delete view"
            >
              ×
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={chipBtn}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {active ? `View: ${active.name}` : 'Views'}
        {active && viewIsDirty && (
          <span
            style={{ marginLeft: 4, color: 'var(--ac)', fontWeight: 700 }}
            title="Current filters differ from the saved view"
          >
            ●
          </span>
        )}
        <span aria-hidden="true" style={{ marginLeft: 6, fontSize: 8 }}>
          ▼
        </span>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 4px)',
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,.16)',
            minWidth: 320,
            zIndex: 10,
            overflow: 'hidden',
          }}
        >
          {ownViews.length === 0 && sharedViews.length === 0 && (
            <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--tm)' }}>
              No saved views yet.
            </div>
          )}
          {ownViews.length > 0 && (
            <>
              <div style={viewsSectionHead}>My views</div>
              {ownViews.map((v) => renderRow(v, true))}
            </>
          )}
          {sharedViews.length > 0 && (
            <>
              <div style={viewsSectionHead}>Shared</div>
              {sharedViews.map((v) => renderRow(v, false))}
            </>
          )}
          <div style={{ borderTop: '1px solid var(--bd)' }}>
            {active && viewIsDirty && (
              <div
                onClick={async () => {
                  await onUpdateActive();
                  setOpen(false);
                }}
                style={{
                  padding: '8px 14px',
                  fontSize: 12,
                  cursor: 'pointer',
                  color: 'var(--ac)',
                  fontWeight: 600,
                }}
                role="menuitem"
              >
                Save changes to "{active.name}"
              </div>
            )}
            <div
              onClick={() => {
                onSaveAs();
                setOpen(false);
              }}
              style={{
                padding: '8px 14px',
                fontSize: 12,
                cursor: 'pointer',
                color: 'var(--ac)',
                fontWeight: 600,
              }}
              role="menuitem"
            >
              + Save as new view…
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
